/* istanbul ignore file */
import {createSelector} from 'reselect'
import {ExistingPersonView} from '@modifi/onboarding-api-clients'
import {PersonLegalRole} from '@modifi/enums'

import {selectPublicRegisteredCompany} from './store/entity'
import {selectResolvedPublicRegisteredCompanyDetails} from './store/entity-resolution'
import {toTitleCase} from '../../utils'

export const selectProviderDetails = createSelector(
  selectPublicRegisteredCompany,
  selectResolvedPublicRegisteredCompanyDetails,
  (company, resolvedCompany) => company?.providerDetails || resolvedCompany?.providerDetails
)

const selectPublicRegisteredCompanyPersons = createSelector(
  selectPublicRegisteredCompany,
  selectResolvedPublicRegisteredCompanyDetails,
  (company, resolvedCompany) => (company?.persons || resolvedCompany?.persons) ?? []
)

const selectPublicRegisteredCompanyPersonRoles = createSelector(
  selectPublicRegisteredCompany,
  selectResolvedPublicRegisteredCompanyDetails,
  (company, resolvedCompany) => (company?.personRoles || resolvedCompany?.personRoles) ?? []
)

// @todo assume every legal role has a matching person?
export const selectPublicRegisteredCompanyLegalRepresentatives = createSelector(
  selectPublicRegisteredCompanyPersons,
  selectPublicRegisteredCompanyPersonRoles,
  (persons, personRoles) => {
    if (personRoles?.length === 0) return []

    const personsMap = persons.reduce(
      (res: Record<string, ExistingPersonView>, person: {providerPersonId: string}) => {
        const {providerPersonId} = person
        return providerPersonId
          ? {
              ...res,
              [providerPersonId]: person,
            }
          : res
      },
      {} as Record<string, ExistingPersonView>
    )

    return personRoles
      .filter((pR: {legalRole: string}) => pR.legalRole === PersonLegalRole.LEGAL_REPRESENTATIVE)
      .map(({_personId, ...personRole}: {_personId: string}) => {
        // eslint-disable-next-line no-underscore-dangle
        const matchingPerson = _personId && personsMap[_personId] ? personsMap[_personId] : null
        return {
          ...personRole,
          ...matchingPerson,
          ...matchingPerson?.address,
          firstName: toTitleCase(matchingPerson?.firstName),
          lastName: toTitleCase(matchingPerson?.lastName),
          providerPersonId: matchingPerson?.providerPersonId!,
        }
      })
  }
)

export const selectPublicRegisteredCompanyUltimateBeneficialOwners = createSelector(
  selectPublicRegisteredCompanyPersons,
  selectPublicRegisteredCompanyPersonRoles,
  (persons, personRoles) => {
    if (personRoles?.length === 0) return []

    const personsMap = persons.reduce(
      (res: Record<string, ExistingPersonView>, person: {providerPersonId: string}) => {
        const {providerPersonId} = person
        return providerPersonId
          ? {
              ...res,
              [providerPersonId]: person,
            }
          : res
      },
      {} as Record<string, ExistingPersonView>
    )

    return personRoles
      .filter(
        (pR: {legalRole: PersonLegalRole}) => pR.legalRole === PersonLegalRole.BENEFICIAL_OWNER
      )
      .map(({_personId, ...personRole}: {_personId: string}) => {
        // eslint-disable-next-line no-underscore-dangle
        const matchingPerson = _personId && personsMap[_personId] ? personsMap[_personId] : null
        return {
          ...personRole,
          ...matchingPerson,
          ...matchingPerson?.address,
          firstName: toTitleCase(matchingPerson?.firstName),
          lastName: toTitleCase(matchingPerson?.lastName),
          providerPersonId: matchingPerson?.providerPersonId!,
        }
      })
  }
)

export const selectHasLegalRepresentatives = createSelector(
  selectPublicRegisteredCompanyLegalRepresentatives,
  legalRepresentatives => legalRepresentatives.length > 0
)

export const selectHasUltimateBeneficialOwners = createSelector(
  selectPublicRegisteredCompanyUltimateBeneficialOwners,
  ultimateBeneficialOwners => ultimateBeneficialOwners.length > 0
)
